import DOMPurify from 'dompurify';

// Inline style for variables that could not be resolved at preview time.
// display:inline keeps the chip in the normal text flow (inline-block disrupts
// line height for surrounding text lines).
const UNRESOLVED_CHIP_STYLE =
  'background:#FFF5F5;border:1px solid #E9EBF4;padding:2px 4px;' +
  'border-radius:6px;font-weight:450;color:#91263B;';

/**
 * Substitutes known merge-variable values into editor HTML, then wraps any
 * remaining {{…}} tokens in a styled chip so the user can tell at a glance
 * which variables weren't available at preview time.
 *
 * @param html     Raw editor HTML (e.g. `value.content` from EmailCompositionValue).
 * @param resolved Map of variable key → substituted value.
 *                 Keys should include the delimiters, e.g. `"{{candidate.first_name}}"`.
 * @returns        Sanitized HTML ready to pass as `previewHtml` to the Editor.
 */
export function buildPreviewHtml(
  html: string,
  resolved: Record<string, string>
): string {
  if (!html) return '';

  let result = html;

  // When the user applies bold/italic/other formatting to text that overlaps
  // a variable token, tiptap splits the token across HTML tags, e.g.:
  //   {{interview.t</strong>ime}}  or  {{interview.<em>time</em>}}
  // Strip any HTML tags found between {{ and }} so the token is whole again
  // before we attempt string substitution.
  result = result.replace(/\{\{(?:[^}]|<[^>]+>)*\}\}/g, (match) =>
    match.replace(/<[^>]+>/g, '')
  );

  for (const [key, value] of Object.entries(resolved)) {
    if (!key || value === undefined) continue;
    const escaped = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    result = result.replace(new RegExp(escaped, 'g'), value);
  }

  // Wrap any remaining {{…}} tokens in a chip span.
  result = result.replace(
    /\{\{[^}]+\}\}/g,
    (match) =>
      `<span style="${UNRESOLVED_CHIP_STYLE}" title="Data not available at preview time">${match}</span>`
  );

  // Preserve blank lines: empty <p> tags (with optional <br>) lose visible
  // height in the preview container when the CSS resets paragraph margins.
  // Replacing with a non-breaking space ensures the empty line stays visible.
  result = result.replace(
    /<p([^>]*)>\s*(?:<br\s*\/?>)?\s*<\/p>/gi,
    '<p$1>&nbsp;</p>'
  );

  return DOMPurify.sanitize(result, { ADD_ATTR: ['target', 'rel'] });
}
